import { DatePipe, UpperCasePipe } from '@angular/common';
import { Component, Directive, ElementRef, ViewEncapsulation, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { Observable, map, of, switchMap } from 'rxjs';
import {
  CONTEXTO_PROVEEDOR, CoberturaProveedor, CredencialesProveedor, DatoProveedor, ENVIO_PROPIO, EnvioPropio, DocumentoProveedor, FichaProveedor, HitoProveedor, PortalProveedorApi, RegistroProveedor,
  SeccionProveedor, TareaProveedor, accionTarea, conRechazos, control, enRevision, envioTexto, esTokenVencido, estadoTexto, fechaCorta, firmaResultados, mensajeError,
  ordenSecciones, pendientes, plazoTexto, seccionEditable, siNo, sinPlazo, tituloTarea,
} from './portal-proveedor';
import { MAX_COMUNAS, RegionCatalogo, ZonaCobertura, alternarComuna, alternarRegion, cubreComuna, etiquetaZona, totalComunas } from '../_models/cobertura';

const MAX_MB = 20;

/** Sin tipos configurados no se restringe (la API decide). */
export const formatoPermitido = (nombre: string, tipos: string[]) =>
  !tipos.length || tipos.map(t => t.toLowerCase()).includes(/\.([^.]+)$/.exec(nombre)?.[1].toLowerCase() ?? '');

/** Credenciales y navegación comunes: propiedades del elemento o, dentro del SPA, el contexto de la ruta. */
@Directive()
abstract class PortalBase {
  readonly token = input<string>();
  readonly tokenPermisos = input<string>();
  readonly tokenExpired = output<void>();
  readonly navegar = output<string>();
  protected readonly api = inject(PortalProveedorApi);
  private readonly ctx = inject(CONTEXTO_PROVEEDOR, { optional: true });
  protected readonly cargando = signal(false);
  protected readonly error = signal('');
  protected readonly cred = computed<CredencialesProveedor | null>(() => {
    const token = this.token(), tokenPermisos = this.tokenPermisos();
    return token && tokenPermisos ? { token, tokenPermisos } : this.ctx?.credenciales() ?? null;
  });

  protected ir(ruta: string) {
    this.navegar.emit(ruta);
    this.ctx?.navegar(ruta);
  }

  protected fallo(e: unknown) {
    this.cargando.set(false);
    if (esTokenVencido(e)) {
      this.error.set('Tu sesión expiró.');
      this.tokenExpired.emit();
    } else this.error.set(mensajeError(e));
  }
}

/** Esqueleto mientras carga: el estilo global (PrimeNG) no entra al Shadow DOM. */
@Component({
  selector: 'app-cargando-proveedor',
  encapsulation: ViewEncapsulation.ShadowDom,
  styleUrl: './proveedor.css',
  template: `
    <div role="status" [attr.aria-label]="texto()" class="esqueletos">
      @for (i of filas(); track i) {
        <div class="tarjeta esqueleto" aria-hidden="true"><span class="hueso corto"></span><span class="hueso"></span><span class="hueso medio"></span></div>
      }
    </div>`,
})
export class CargandoProveedorComponent {
  readonly texto = input('Cargando…');
  readonly cantidad = input(3);
  protected readonly filas = computed(() => Array.from({ length: this.cantidad() }, (_, i) => i));
}

/** Estado vacío con ilustración y llamado a la acción (ng-content). */
@Component({
  selector: 'app-vacio-proveedor',
  encapsulation: ViewEncapsulation.ShadowDom,
  styleUrl: './proveedor.css',
  template: `
    <div class="vacio">
      <svg class="ilustracion" viewBox="0 0 160 120" aria-hidden="true">
        <circle cx="80" cy="60" r="52" fill="#eff6ff" />
        <circle cx="132" cy="22" r="5" fill="#bfdbfe" /><circle cx="26" cy="92" r="4" fill="#fde68a" /><circle cx="140" cy="88" r="3" fill="#bbf7d0" />
        @switch (icono()) {
          @case ('tareas') {
            <rect x="50" y="26" width="60" height="72" rx="8" fill="#fff" stroke="#93c5fd" stroke-width="2" />
            <rect x="66" y="20" width="28" height="12" rx="4" fill="#2563eb" />
            <circle cx="80" cy="64" r="18" fill="#dcfce7" /><path d="M71 64l6 6 12-13" fill="none" stroke="#16a34a" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" />
          }
          @case ('ficha') {
            <path d="M40 40a6 6 0 0 1 6-6h22l8 8h38a6 6 0 0 1 6 6v42a6 6 0 0 1-6 6H46a6 6 0 0 1-6-6z" fill="#fff" stroke="#93c5fd" stroke-width="2" />
            <rect x="56" y="58" width="48" height="6" rx="3" fill="#bfdbfe" /><rect x="56" y="72" width="32" height="6" rx="3" fill="#dbeafe" />
          }
          @default {
            <rect x="34" y="44" width="40" height="52" rx="4" fill="#fff" stroke="#93c5fd" stroke-width="2" />
            <rect x="82" y="30" width="44" height="66" rx="4" fill="#fff" stroke="#2563eb" stroke-width="2" />
            <path d="M42 56h24M42 66h24M42 76h16M90 42h28M90 52h28M90 62h20" stroke="#bfdbfe" stroke-width="4" stroke-linecap="round" />
            <circle cx="112" cy="82" r="11" fill="#2563eb" /><path d="M107 82l4 4 6-7" fill="none" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />
          }
        }
      </svg>
      <h3>{{ titulo() }}</h3>
      <p class="sutil">{{ texto() }}</p>
      <ng-content />
    </div>`,
})
export class VacioProveedorComponent {
  readonly titulo = input.required<string>();
  readonly texto = input.required<string>();
  readonly icono = input<'registros' | 'tareas' | 'ficha'>('registros');
}

@Component({
  selector: 'app-mis-registros',
  encapsulation: ViewEncapsulation.ShadowDom,
  styleUrl: './proveedor.css',
  imports: [CargandoProveedorComponent, VacioProveedorComponent],
  template: `
    <section aria-labelledby="titulo">
      <h2 id="titulo">Mis registros</h2>
      @if (error()) { <p role="alert" class="error">{{ error() }}</p> }
      @if (cargando()) { <app-cargando-proveedor texto="Cargando tus registros…" /> }
      @else if (!registros().length && !error()) {
        <app-vacio-proveedor titulo="Tu vitrina ante nuevos clientes empieza aquí"
          texto="Aún no tienes registros con clientes. Cuando una empresa te invite a su registro de proveedores, la verás aquí junto a tu estado de acreditación.">
          <ul class="beneficios">
            <li>Tu información al día en un solo lugar, para todos tus clientes.</li>
            <li>Responde más rápido lo que te piden y evita bloqueos.</li>
            <li>Destaca como proveedor acreditado.</li>
          </ul>
        </app-vacio-proveedor>
      }
      <ul>
        @for (r of registros(); track r.clienteId) {
          <li>
            <button type="button" class="tarjeta registro" (click)="ir('/proveedor/clientes/' + r.clienteId)">
              <span class="inicial" aria-hidden="true">{{ (r.cliente || 'C').charAt(0) }}</span>
              <span class="datos">
                <strong>{{ r.cliente || 'Cliente ' + r.clienteId }}</strong>
                <span class="sutil">Clase {{ r.clasificacion }}</span>
                @if (r.bloqueo) { <span class="aviso">{{ r.bloqueo }}</span> }
              </span>
              <span class="chips">
                <span class="badge" [attr.data-estado]="r.estadoAcreditacion">{{ estado(r.estadoAcreditacion) }}</span>
                @if (r.tareasAbiertas) { <span class="badge" data-estado="pendiente">{{ r.tareasAbiertas }} {{ r.tareasAbiertas === 1 ? 'tarea pendiente' : 'tareas pendientes' }}</span> }
              </span>
              <span class="flecha" aria-hidden="true">›</span>
            </button>
          </li>
        }
      </ul>
    </section>`,
})
export class MisRegistrosComponent extends PortalBase {
  protected readonly registros = signal<RegistroProveedor[]>([]);
  protected readonly estado = estadoTexto;

  constructor() {
    super();
    effect(() => {
      const c = this.cred();
      if (!c) return;
      untracked(() => {
        this.cargando.set(true);
        this.api.misRegistros(c).subscribe({ next: r => (this.registros.set(r), this.cargando.set(false), this.error.set('')), error: e => this.fallo(e) });
      });
    });
  }
}

/** "Lo que debes completar" de una acreditación: plazo, mensaje del cliente y secciones con su estado. */
@Component({
  selector: 'app-acreditacion-proveedor',
  encapsulation: ViewEncapsulation.ShadowDom,
  styleUrl: './proveedor.css',
  template: `
    @let t = tarea();
    @let titulo = tituloTarea(t);
    <article class="tarjeta acreditacion" [attr.aria-label]="titulo + ' con ' + (t.cliente || 'tu cliente')">
      <header class="cabecera">
        <div>
          <strong>{{ titulo }}@if (conCliente()) { con {{ t.cliente || 'Cliente ' + t.clienteId }} }</strong>
          <span class="sutil"> · {{ t.claseNombre }}</span>
        </div>
        @if (sinPlazo(t)) {
          <span class="badge" data-estado="pendiente">Sin plazo@if (t.desde) { · pendiente desde {{ fecha(t.desde) }} }</span>
        } @else {
          @let revision = enRevision(t);
          <span class="badge" [attr.data-estado]="revision ? 'acreditado' : t.diasRestantes < 0 ? 'rechazado' : t.diasRestantes <= 5 ? 'pendiente' : null">
            {{ plazo(t) }}@if (!revision && t.diasRestantes >= 0) { · {{ fecha(t.venceEn) }} }
          </span>
        }
      </header>
      @if (t.mensaje) { <p class="mensaje">{{ t.mensaje }}</p> }
      @if (t.items.length) {
        @let listas = t.items.length - t.faltan;
        <div class="progreso">
          <p class="sutil">Lo que debes completar ({{ listas }} de {{ t.items.length }} listas):</p>
          <div class="barra" role="progressbar" [attr.aria-valuenow]="listas" aria-valuemin="0" [attr.aria-valuemax]="t.items.length"
            [attr.aria-label]="listas + ' de ' + t.items.length + ' secciones listas'"><span [style.width.%]="(listas / t.items.length) * 100"></span></div>
        </div>
        <ul class="items">
          @for (i of t.items; track i.seccionId) {
            @let estadoItem = i.estado === 'completada' ? 'lista' : i.enRevision ? 'revision' : 'pendiente';
            <li [attr.data-estado]="estadoItem">
              <span class="punto" aria-hidden="true">{{ estadoItem === 'lista' ? '✓' : '' }}</span>
              <span class="item-nombre">
                @if (enlazar() && estadoItem === 'pendiente') {
                  <button type="button" class="enlace-item" (click)="irSeccion.emit(i.seccionId)">{{ i.nombre }}</button>
                } @else { {{ i.nombre }} }
                @if (i.observacion) { <span [class]="t.tipo === 'renovacion' ? 'aviso' : 'error'"> · {{ t.tipo === 'renovacion' ? '' : 'Corregir: ' }}{{ i.observacion }}</span> }</span>
              <span class="item-estado">{{ estadoItem === 'lista' ? 'Lista' : estadoItem === 'revision' ? 'Enviada, en revisión' : 'Pendiente' }}</span>
            </li>
          }
        </ul>
      } @else {
        <p class="sutil">No tienes secciones por completar; el cliente está revisando tu información.</p>
      }
      <ng-content />
    </article>`,
})
export class AcreditacionProveedorComponent {
  readonly tarea = input.required<TareaProveedor>();
  readonly conCliente = input(true);
  /** Dentro de la ficha, cada sección pendiente lleva a su tarjeta. */
  readonly enlazar = input(false);
  readonly irSeccion = output<string>();
  protected readonly plazo = plazoTexto;
  protected readonly tituloTarea = tituloTarea;
  protected readonly sinPlazo = sinPlazo;
  protected readonly enRevision = enRevision;
  protected readonly fecha = fechaCorta;
}

@Component({
  selector: 'app-mis-tareas-proveedor',
  encapsulation: ViewEncapsulation.ShadowDom,
  styleUrl: './proveedor.css',
  imports: [AcreditacionProveedorComponent, CargandoProveedorComponent, VacioProveedorComponent],
  template: `
    <section aria-labelledby="titulo">
      <h2 id="titulo">Mis tareas</h2>
      @if (error()) { <p role="alert" class="error">{{ error() }}</p> }
      @if (cargando()) { <app-cargando-proveedor texto="Cargando tus tareas…" [cantidad]="2" /> }
      @else if (!tareas().length && !error()) {
        <app-vacio-proveedor icono="tareas" titulo="¡Estás al día!"
          texto="No tienes tareas pendientes. Cuando un cliente te pida acreditarte o actualizar tu información, te avisaremos por correo y la verás aquí.">
          <p><button type="button" class="btn" (click)="ir('/proveedor')">Ver mis registros</button></p>
        </app-vacio-proveedor>
      }
      <ul>
        @for (t of tareas(); track t.id) {
          <li>
            <app-acreditacion-proveedor [tarea]="t">
              @if (t.faltan) { <p><button type="button" class="btn" (click)="ir('/proveedor/clientes/' + t.clienteId)">{{ accion(t) }}</button></p> }
            </app-acreditacion-proveedor>
          </li>
        }
      </ul>
    </section>`,
})
export class MisTareasProveedorComponent extends PortalBase {
  readonly tareasPendientes = output<number>();
  protected readonly tareas = signal<TareaProveedor[]>([]);
  protected readonly accion = accionTarea;

  constructor() {
    super();
    effect(() => {
      const c = this.cred();
      if (!c) return;
      untracked(() => {
        this.cargando.set(true);
        this.api.tareas(c).subscribe({
          next: t => {
            this.tareas.set(t);
            this.cargando.set(false);
            this.error.set('');
            this.tareasPendientes.emit(t.filter(x => x.faltan > 0).length);
          },
          error: e => this.fallo(e),
        });
      });
    });
  }
}

/** Cobertura de la empresa: región completa o algunas comunas. Edita una copia; `guardar` emite las zonas y la API valida contra el catálogo. */
@Component({
  selector: 'app-cobertura-proveedor',
  encapsulation: ViewEncapsulation.ShadowDom,
  styleUrl: './proveedor.css',
  template: `
    @if (!editando()) {
      <div class="chips" aria-label="Tu cobertura">
        @for (z of cobertura().zonas; track z.region) { <span class="badge">{{ etiqueta(z) }}</span> }
        @empty { <span class="sutil">Aún no indicas dónde trabajas.</span> }
      </div>
      @if (cobertura().catalogo.length) {
        <div class="accion"><button type="button" class="btn secundario" [disabled]="ocupado()" (click)="editar()">Editar cobertura</button></div>
      } @else { <p class="sutil">La cobertura no está disponible en este momento. Inténtalo más tarde.</p> }
    } @else {
      <p class="sutil">Marca la región completa o elige solo algunas de sus comunas.</p>
      <div class="cobertura">
        <ul class="regiones" aria-label="Regiones">
          @for (r of cobertura().catalogo; track r.nombre) {
            @let z = zona(r.nombre);
            <li [class.activa]="activa() === r.nombre">
              <label><input type="checkbox" [checked]="!!z && !z.comunas.length" [indeterminate]="!!z?.comunas?.length" (change)="alternarRegion(r.nombre)" />
                {{ r.nombre }}</label>
              @if (r.comunas.length) {
                <button type="button" class="enlace-item" [attr.aria-label]="'Comunas de ' + r.nombre" (click)="abrir(r.nombre)">
                  {{ z?.comunas?.length ? z!.comunas.length + ' de ' + r.comunas.length : z ? 'Toda' : 'Comunas' }} ›</button>
              }
            </li>
          }
        </ul>
        @if (region(); as r) {
          <fieldset class="comunas">
            <legend>{{ r.nombre }}</legend>
            <label><input type="checkbox" [checked]="!!zona(r.nombre) && !zona(r.nombre)!.comunas.length" (change)="alternarRegion(r.nombre)" /> Toda la región</label>
            <input type="search" placeholder="Buscar comuna…" aria-label="Buscar comuna" [value]="filtro()" (input)="filtro.set($any($event.target).value)" />
            <div class="lista">
              @for (c of comunasVisibles(); track c) {
                <label><input type="checkbox" [checked]="cubre(r.nombre, c)" (change)="alternarComuna(r, c)" /> {{ c }}</label>
              } @empty { <span class="sutil">Sin resultados.</span> }
            </div>
          </fieldset>
        } @else { <p class="sutil comunas">Elige una región para ver sus comunas.</p> }
      </div>
      @if (excede()) { <p role="alert" class="error">Puedes indicar hasta {{ maxComunas }} comunas; marca regiones completas para cubrir más.</p> }
      <div class="accion fin">
        <button type="button" class="btn secundario" [disabled]="ocupado()" (click)="editando.set(false)">Cancelar</button>
        <button type="button" class="btn" [disabled]="ocupado() || excede()" (click)="guardar.emit(borrador())">Guardar cobertura</button>
      </div>
    }`,
})
export class CoberturaProveedorComponent {
  readonly cobertura = input.required<CoberturaProveedor>();
  readonly ocupado = input(false);
  readonly guardar = output<ZonaCobertura[]>();
  protected readonly editando = signal(false);
  protected readonly borrador = signal<ZonaCobertura[]>([]);
  protected readonly activa = signal<string | null>(null);
  protected readonly filtro = signal('');
  protected readonly maxComunas = MAX_COMUNAS;
  protected readonly etiqueta = etiquetaZona;
  protected readonly region = computed(() => this.cobertura().catalogo.find(r => r.nombre === this.activa()) ?? null);
  protected readonly comunasVisibles = computed(() => {
    const f = this.filtro().trim().toLocaleLowerCase('es');
    return (this.region()?.comunas ?? []).filter(c => !f || c.toLocaleLowerCase('es').includes(f));
  });
  protected readonly excede = computed(() => totalComunas(this.borrador()) > MAX_COMUNAS);

  constructor() {
    // Al guardar llega la cobertura nueva: se cierra el editor.
    effect(() => { this.cobertura(); untracked(() => this.editando.set(false)); });
  }

  protected editar() {
    this.borrador.set(this.cobertura().zonas.map(z => ({ region: z.region, comunas: [...z.comunas] })));
    this.activa.set(null);
    this.filtro.set('');
    this.editando.set(true);
  }
  protected zona = (region: string) => this.borrador().find(z => z.region === region);
  protected cubre = (region: string, comuna: string) => cubreComuna(this.borrador(), region, comuna);
  protected abrir(region: string) { this.activa.set(region); this.filtro.set(''); }
  protected alternarRegion(region: string) { this.borrador.update(z => alternarRegion(z, region)); }
  protected alternarComuna(r: RegionCatalogo, comuna: string) { this.borrador.update(z => alternarComuna(z, r.nombre, comuna, r.comunas)); }
}

@Component({
  selector: 'app-mi-ficha',
  encapsulation: ViewEncapsulation.ShadowDom,
  styleUrl: './proveedor.css',
  imports: [AcreditacionProveedorComponent, CargandoProveedorComponent, CoberturaProveedorComponent, VacioProveedorComponent, UpperCasePipe, DatePipe],
  host: { '(window:beforeunload)': 'antesDeSalir($event)' },
  template: `
    <section aria-labelledby="titulo">
      <header class="encabezado">
        <button type="button" class="volver" (click)="ir('/proveedor')">‹ Mis registros</button>
        <h2 id="titulo">{{ ficha()?.cliente || 'Mi ficha' }}</h2>
        @if (ficha(); as f) {
          <p class="meta"><span class="badge" [attr.data-estado]="f.estadoAcreditacion">{{ estado(f.estadoAcreditacion) }}</span>
            <span class="sutil">Clase {{ f.clasificacion }}</span></p>
        }
      </header>
      @if (resultado(); as r) {
        <div class="resultado" [attr.data-estado]="r.estado" role="status">
          @if (r.estado === 'aprobado') {
            <strong>Acreditación aprobada</strong>
            <span>Tu cliente aprobó tu acreditación como {{ r.claseNombre }}@if (r.fecha) { el {{ r.fecha | date: 'dd-MM-yyyy' }} }.</span>
          } @else {
            <strong>Acreditación rechazada</strong>
            <span>Tu cliente rechazó tu acreditación como {{ r.claseNombre }}@if (r.fecha) { el {{ r.fecha | date: 'dd-MM-yyyy' }} }.</span>
            @if (r.motivo) { <span>Motivo: {{ r.motivo }}</span> }
            <span class="sutil">Si tienes dudas, contacta a tu cliente.</span>
          }
        </div>
      }
      @if (error()) { <p role="alert" class="error">{{ error() }}</p> }
      @if (exito()) { <p role="status" class="ok">{{ exito() }}</p> }
      @if (cargando() && !ficha()) { <app-cargando-proveedor texto="Cargando tu ficha…" [cantidad]="4" /> }
      @if (ficha(); as f) {
        @for (t of f.tareas; track t.id) { <app-acreditacion-proveedor [tarea]="t" [conCliente]="false" [enlazar]="true" (irSeccion)="irSeccion($event)" /> }
        @if (!f.secciones.length) {
          <app-vacio-proveedor icono="ficha" titulo="Tu ficha está en buenas manos"
            texto="Este cliente aún no te comparte secciones para completar desde el portal. Si te pide información, te avisaremos por correo." />
        }
        @for (s of secciones(); track s.seccionId) {
          @let p = pendientes(conBorrador(s));
          @let locales = cambiosDe(s).length;
          <details class="seccion" [id]="'s-' + s.seccionId" [open]="abiertas().has(s.seccionId)" (toggle)="alternar(s.seccionId, $any($event.target).open)">
            <summary>
              <span class="chevron" aria-hidden="true">›</span>
              <span class="titulo-seccion">
                <h3>{{ s.nombre }}</h3>
                @if (s.ayuda) { <span class="sub">{{ s.ayuda }}</span> }
              </span>
              <span class="chips">
                @if (enAcreditacion().has(s.seccionId)) { <span class="badge" data-estado="en_acreditacion">Acreditación</span> }
                @if (s.porActualizar) { <span class="badge" data-estado="por_actualizar">Por actualizar</span> }
                @if (s.envio) { <span class="badge" [attr.data-estado]="s.envio.estado">{{ envioTexto(s) }}</span> }
                @if (!editable(s)) { <span class="badge">{{ s.envio?.estado === 'pendiente' ? 'En revisión' : 'Solo lectura' }}</span> }
                @else if (p.faltan) { <span class="badge" data-estado="pendiente">Faltan {{ p.faltan }} de {{ p.total }}</span> }
                @else if (p.total) { <span class="badge" data-estado="acreditado">Completa</span> }
              </span>
            </summary>
            <div class="cuerpo">
              @if (s.envio?.estado === 'rechazado' && s.envio?.observacion) {
                <p role="alert" class="error">{{ conRechazos(s) ? 'El cliente rechazó lo marcado abajo; corrígelo y vuelve a enviar la sección.' : 'Motivo del rechazo: ' + s.envio?.observacion }}</p>
              }
              @if (s.envio?.estado === 'pendiente') { <p class="sutil">Enviaste esta sección; podrás editarla cuando el cliente la revise.</p> }
              @if (!s.datos.length && !s.documentos.length) { <p class="sutil">Esta sección no tiene datos ni documentos solicitados.</p> }
              @if (s.datos.length) {
                <h4 class="grupo">Datos solicitados</h4>
                <dl class="campos">
                  @for (d of s.datos; track d.campoId) {
                    @let c = editable(s) ? control(d) : null;
                    @let k = clave(s, d);
                    @let v = valorDe(s, d);
                    <div class="campo" [class.ancho]="c === 'textarea'">
                      <dt><label [attr.for]="'d-' + k">{{ d.etiqueta }}</label>
                        @if (c) { <span class="badge" [attr.data-estado]="d.requerido ? 'obligatorio' : null">{{ d.requerido ? 'Obligatorio' : 'Opcional' }}</span> }
                        @if (d.ayuda) { <details class="ayuda"><summary [attr.aria-label]="'Ayuda: ' + d.etiqueta">?</summary><p class="sutil">{{ d.ayuda }}</p></details> }</dt>
                      @switch (c) {
                        @case (null) {
                          <dd>{{ d.tipo === 'checkbox' || d.tipo === 'casilla' ? siNo(d.valor) : d.valor || '—' }}</dd>
                          @if (editable(s)) { <dd class="sutil nota">Lo actualiza tu cliente</dd> }
                        }
                        @case ('textarea') {
                          <dd><textarea [id]="'d-' + k" rows="3" maxlength="1000" [value]="v" [placeholder]="d.placeholder ?? ''"
                            [attr.aria-invalid]="!!d.rechazo" (input)="poner(k, $any($event.target).value)"></textarea></dd>
                        }
                        @case ('select') {
                          <dd><select [id]="'d-' + k" [attr.aria-invalid]="!!d.rechazo" (change)="poner(k, $any($event.target).value)">
                            <option value="" [selected]="!v">Selecciona…</option>
                            @for (o of d.opciones; track o) { <option [value]="o" [selected]="o === v">{{ o }}</option> }
                          </select></dd>
                        }
                        @case ('checkbox') {
                          <dd><fieldset class="si-no" [attr.aria-label]="d.etiqueta">
                            @for (o of ['true', 'false']; track o) {
                              <label><input type="radio" [name]="'d-' + k" [id]="o === 'true' ? 'd-' + k : null" [value]="o" [checked]="v === o"
                                (change)="poner(k, o)" />{{ siNo(o) }}</label>
                            }
                          </fieldset></dd>
                        }
                        @default {
                          <dd><input [id]="'d-' + k" [type]="c" [value]="v" [placeholder]="d.placeholder ?? (c === 'tel' ? '+56 9 1234 5678' : '')" maxlength="1000"
                            [attr.inputmode]="c === 'tel' ? 'tel' : null" [attr.aria-invalid]="!!d.rechazo" (input)="poner(k, $any($event.target).value)" /></dd>
                        }
                      }
                      @if (c) {
                        @if (d.rechazo) { <dd class="error">Rechazado: {{ d.rechazo }}</dd> }
                        @if (borrador()[k] !== undefined && borrador()[k] !== guardado(d)) { <dd class="borrador">Sin guardar</dd> }
                        @else if (d.propuesto != null) { <dd class="borrador">Guardado en el borrador, sin enviar</dd> }
                      }
                    </div>
                  }
                </dl>
              }
              @if (s.documentos.length) {
                <h4 class="grupo">Documentos</h4>
                <div class="docs">
                  @for (doc of s.documentos; track doc.documentoSolicitaId) {
                    @let hay = doc.cargado || !!doc.propuestoNombre;
                    <div class="doc" [class.requerido]="doc.requerido && editable(s) && !hay">
                      <div class="doc-cabecera">
                        <p class="doc-nombre">{{ doc.nombre }}
                          @if (doc.ayuda) { <details class="ayuda"><summary [attr.aria-label]="'Ayuda: ' + doc.nombre">?</summary><p class="sutil">{{ doc.ayuda }}</p></details> }</p>
                        <span class="chips">
                          @if (doc.vencido) { <span class="badge" data-estado="por_actualizar">Vencido: súbelo de nuevo</span> }
                          @else if (doc.vence) { <span class="badge">Vence {{ fecha(doc.vence) }}</span> }
                          @if (editable(s)) { <span class="badge" [attr.data-estado]="doc.requerido ? 'obligatorio' : null">{{ doc.requerido ? 'Obligatorio' : 'Opcional' }}</span> }
                        </span>
                      </div>
                      @if (doc.placeholder && !hay) { <p class="doc-desc">{{ doc.placeholder }}</p> }
                      @if (doc.rechazo) { <p class="error">Rechazado: {{ doc.rechazo }}</p> }
                      @if (doc.propuestoNombre) {
                        <p class="archivo pendiente"><span class="icono" aria-hidden="true">📄</span>
                          <span><strong>{{ doc.propuestoNombre }}</strong>@if (doc.propuestoVencimiento) { · vence {{ fecha(doc.propuestoVencimiento) }} }</span>
                          <span class="badge">Borrador sin enviar</span></p>
                      } @else if (doc.cargado) {
                        <p class="archivo cargado"><span class="icono" aria-hidden="true">✓</span><span>Archivo cargado</span></p>
                      } @else if (!editable(s)) {
                        <p class="sutil">No hay archivo subido</p>
                      }
                      @if (editable(s)) {
                        @if (doc.solicitaVencimiento) {
                          <label class="fecha">Fecha de vencimiento *
                            <input type="date" [id]="'v-' + doc.documentoSolicitaId" />
                          </label>
                        }
                        <label class="dropzone" [class.compacta]="hay" [class.activa]="arrastrando() === doc.documentoSolicitaId" [class.deshabilitado]="cargando()"
                          (dragover)="$event.preventDefault(); arrastrando.set(doc.documentoSolicitaId)" (dragleave)="arrastrando.set(null)"
                          (drop)="$event.preventDefault(); arrastrando.set(null); subirArchivo(doc, $event.dataTransfer?.files?.[0])">
                          <svg class="nube" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 16V8m0 0-3 3m3-3 3 3M7 18h10a4 4 0 0 0 .6-7.96A6 6 0 0 0 6.2 9.2 4.5 4.5 0 0 0 7 18z"
                            fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" /></svg>
                          <span class="drop-texto">{{ hay ? 'Arrastra un archivo para reemplazarlo o' : 'Arrastra tu archivo aquí o' }}
                            <span class="enlace">selecciónalo</span></span>
                          <span class="drop-ayuda">{{ tipos(doc) | uppercase }} · máx. {{ maxMb(doc) }} MB</span>
                          <input #a type="file" class="oculto" [id]="'f-' + doc.documentoSolicitaId" [accept]="accept(doc)" [disabled]="cargando()"
                            (change)="subirArchivo(doc, a.files?.[0]); a.value = ''" />
                        </label>
                      }
                    </div>
                  }
                </div>
              }
              @if (editable(s) && (locales || s.envio?.cambios)) {
                <div class="enviar">
                  <span class="sutil">
                    @if (locales) { {{ locales }} {{ locales === 1 ? 'cambio sin guardar' : 'cambios sin guardar' }} }
                    @else { {{ s.envio?.cambios }} {{ s.envio?.cambios === 1 ? 'cambio listo para enviar' : 'cambios listos para enviar' }} }
                  </span>
                  @if (locales) { <button type="button" class="btn secundario" [disabled]="cargando()" (click)="guardar(s)">Guardar borrador</button> }
                  <button type="button" class="btn" [disabled]="cargando()" (click)="enviarSeccion(s)">
                    {{ s.requiereAprobacion ? 'Enviar a aprobación' : 'Enviar y actualizar mi ficha' }}</button>
                </div>
              }
            </div>
          </details>
        }
        <details class="seccion" (toggle)="$any($event.target).open && cargarCobertura()">
          <summary>
            <span class="chevron" aria-hidden="true">›</span>
            <span class="titulo-seccion"><h3>Mi cobertura</h3>
              <span class="sub">Regiones y comunas donde trabajas. La ven todos tus clientes y se actualiza al guardar.</span></span>
          </summary>
          <div class="cuerpo">
            @if (cobertura(); as cob) { <app-cobertura-proveedor [cobertura]="cob" [ocupado]="cargando()" (guardar)="guardarCobertura($event)" /> }
            @else { <app-cargando-proveedor texto="Cargando tu cobertura…" [cantidad]="1" /> }
          </div>
        </details>
        @if (f.secciones.length) {
          <details class="seccion" [open]="enviosAbierto()" (toggle)="abrirEnvios($any($event.target).open)">
            <summary>
              <span class="chevron" aria-hidden="true">›</span>
              <span class="titulo-seccion"><h3>Mis envíos</h3><span class="sub">Lo que enviaste a tu cliente y su resultado</span></span>
              @if (resultadoNuevo()) { <span class="chips"><span class="badge" data-estado="en_acreditacion">Resultado nuevo</span></span> }
            </summary>
            <div class="cuerpo">
              @if (envios(); as l) {
                @for (e of l; track $index) {
                  <div class="doc">
                    <div class="doc-cabecera">
                      <strong>{{ e.seccion }}</strong>
                      <span class="badge" [attr.data-estado]="e.estado === 'aplicado' ? 'acreditado' : e.estado">{{ envioPropio[e.estado] }}</span>
                    </div>
                    <span class="sutil">Enviado el {{ e.enviadoEn | date: 'dd-MM-yyyy HH:mm' }}@if (e.cerradoEn) { · aplicado el {{ e.cerradoEn | date: 'dd-MM-yyyy' }} }</span>
                    @if (e.items.length) {
                      <ul class="items">
                        @for (i of e.items; track $index) {
                          <li [attr.data-estado]="i.resultado === 'aprobado' ? 'lista' : i.resultado === 'rechazado' ? 'rechazado' : 'revision'">
                            <span class="punto" aria-hidden="true">{{ i.resultado === 'aprobado' ? '✓' : i.resultado === 'rechazado' ? '✕' : '' }}</span>
                            <span class="item-nombre">{{ i.nombre }}@if (i.archivo) { <span class="sutil"> · {{ i.archivo }}</span> }
                              @if (i.comentario) { <span class="error"> · {{ i.comentario }}</span> }</span>
                            <span class="item-estado">{{ i.resultado === 'aprobado' ? 'Aprobado' : i.resultado === 'rechazado' ? 'Rechazado' : 'En revisión' }}</span>
                          </li>
                        }
                      </ul>
                    }
                    @if (e.observacion) { <span class="error">Motivo: {{ e.observacion }}</span> }
                  </div>
                } @empty {
                  <p class="sutil">Aún no has enviado cambios a este cliente.</p>
                }
              } @else {
                <app-cargando-proveedor texto="Cargando tus envíos…" [cantidad]="2" />
              }
            </div>
          </details>
          <details class="seccion" (toggle)="$any($event.target).open && cargarHistorial()">
            <summary>
              <span class="chevron" aria-hidden="true">›</span>
              <span class="titulo-seccion"><h3>Historial</h3><span class="sub">Tu acreditación y colaboración con este cliente</span></span>
            </summary>
            <div class="cuerpo">
              @if (historial(); as l) {
                <ol class="historial">
                  @for (h of l; track $index) {
                    <li [attr.data-tipo]="h.tipo">
                      <span class="sutil">{{ h.fecha | date: 'dd-MM-yyyy HH:mm' }}</span>
                      <strong>{{ h.titulo }}</strong>
                      @if (h.detalle) { <span>{{ h.detalle }}</span> }
                    </li>
                  } @empty {
                    <li class="sutil">Aún no hay movimientos con este cliente.</li>
                  }
                </ol>
              } @else {
                <app-cargando-proveedor texto="Cargando tu historial…" [cantidad]="2" />
              }
            </div>
          </details>
        }
      }
    </section>`,
})
export class MiFichaComponent extends PortalBase {
  readonly clienteId = input<string>();
  private readonly ruta = inject(ActivatedRoute, { optional: true });
  private readonly idRuta = toSignal(this.ruta?.paramMap.pipe(map(p => p.get('clienteId') ?? undefined)) ?? of(undefined));
  private readonly id = computed(() => this.clienteId() ?? this.idRuta());
  protected readonly ficha = signal<FichaProveedor | null>(null);
  protected readonly exito = signal('');
  protected readonly estado = estadoTexto;
  protected readonly envioTexto = envioTexto;
  protected readonly editable = seccionEditable;
  protected readonly conRechazos = conRechazos;
  protected readonly control = control;
  protected readonly pendientes = pendientes;
  protected readonly siNo = siNo;
  protected readonly envios = signal<EnvioPropio[] | null>(null);
  protected readonly envioPropio = ENVIO_PROPIO;
  protected readonly enviosAbierto = signal(false);
  protected readonly resultadoNuevo = signal(false);
  protected readonly historial = signal<HitoProveedor[] | null>(null);
  protected readonly abiertas = signal(new Set<string>());
  /** Valores escritos y aún no guardados, por `seccionId/campoId`. */
  protected readonly borrador = signal<Record<string, string>>({});
  private orden: string[] = [];
  private enviosPedidos = false;
  private historialPedido = false;
  private readonly host = inject(ElementRef<HTMLElement>);

  /** El orden se fija al abrir la ficha: las tarjetas no saltan mientras el socio completa. */
  protected readonly secciones = computed(() => {
    const l = this.ficha()?.secciones ?? [], i = (s: SeccionProveedor) => this.orden.indexOf(s.seccionId) >>> 0;
    return [...l].sort((a, b) => i(a) - i(b));
  });
  protected readonly enAcreditacion = computed(() =>
    new Set(this.ficha()?.tareas.filter(t => !sinPlazo(t)).flatMap(t => t.items.map(i => i.seccionId)) ?? []));
  /** El resultado de la última acreditación cerrada, si no hay otra en curso. */
  protected readonly resultado = computed(() => {
    const f = this.ficha();
    return f?.resultado && !f.tareas.some(t => !sinPlazo(t)) ? f.resultado : null;
  });
  private readonly hayCambios = computed(() => (this.ficha()?.secciones ?? []).some(s => this.cambiosDe(s).length));

  constructor() {
    super();
    effect(() => {
      const c = this.cred(), id = this.id();
      if (c && id) untracked(() => this.cargar(c, id));
    });
  }

  private cargar(c: CredencialesProveedor, id: string) {
    if (!/^\d{1,10}$/.test(id)) return this.error.set('No encontramos este registro.');
    this.cargando.set(true);
    this.api.ficha(c, id).subscribe({
      next: f => {
        const orden = ordenSecciones(f.secciones);
        this.orden = orden.map(s => s.seccionId);
        this.abiertas.set(new Set(orden.filter(s => pendientes(s).faltan || s.envio?.estado === 'rechazado').map(s => s.seccionId)));
        this.ficha.set(f);
        this.cargando.set(false);
        this.cargarEnvios();
      },
      error: e => this.fallo(e),
    });
  }

  protected override ir(ruta: string) {
    if (this.hayCambios() && !confirm('Tienes cambios sin guardar. ¿Salir de todas formas?')) return;
    super.ir(ruta);
  }

  protected antesDeSalir(e: BeforeUnloadEvent) {
    if (this.hayCambios()) e.preventDefault();
  }

  protected alternar(id: string, abierta: boolean) {
    if (abierta === this.abiertas().has(id)) return;
    const s = new Set(this.abiertas());
    if (abierta) s.add(id); else s.delete(id);
    this.abiertas.set(s);
  }

  protected irSeccion(id: string) {
    this.alternar(id, true);
    setTimeout(() => {
      const el = this.host.nativeElement.shadowRoot?.getElementById('s-' + id);
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el?.querySelector('summary')?.focus({ preventScroll: true });
    });
  }

  protected clave = (s: SeccionProveedor, d: DatoProveedor) => `${s.seccionId}/${d.campoId}`;
  protected guardado = (d: DatoProveedor) => d.propuesto ?? d.valor ?? '';
  protected valorDe = (s: SeccionProveedor, d: DatoProveedor) => this.borrador()[this.clave(s, d)] ?? this.guardado(d);
  protected conBorrador = (s: SeccionProveedor): SeccionProveedor => ({ ...s, datos: s.datos.map(d => ({ ...d, propuesto: this.valorDe(s, d) })) });
  protected poner(k: string, v: string) { this.borrador.update(b => ({ ...b, [k]: v })); }
  protected cambiosDe(s: SeccionProveedor) {
    const b = this.borrador();
    return s.datos.filter(d => { const v = b[this.clave(s, d)]; return v !== undefined && v !== this.guardado(d); })
      .map(d => ({ campoId: d.campoId, valor: b[this.clave(s, d)] }));
  }
  private limpiar(s: SeccionProveedor) {
    this.borrador.update(b => Object.fromEntries(Object.entries(b).filter(([k]) => !k.startsWith(s.seccionId + '/'))));
  }

  protected cargarEnvios() {
    const c = this.cred(), id = this.id();
    if (!c || !id || this.enviosPedidos) return;
    this.enviosPedidos = true;
    this.api.envios(c, id).subscribe({
      next: l => {
        this.envios.set(l);
        const firma = firmaResultados(l);
        if (firma && firma !== this.visto()) { this.resultadoNuevo.set(true); this.enviosAbierto.set(true); this.marcarVisto(); }
      },
      error: e => { this.enviosPedidos = false; this.envios.set([]); this.fallo(e); },
    });
  }

  protected abrirEnvios(abierto: boolean) {
    this.enviosAbierto.set(abierto);
    if (abierto) { this.cargarEnvios(); this.marcarVisto(); }
  }

  /** Último resultado visto por envío en este navegador; sin almacenamiento, siempre se avisa. */
  private claveVisto = () => `portal-proveedor:envios:${this.id()}`;
  private visto() { try { return localStorage.getItem(this.claveVisto()); } catch { return null; } }
  private marcarVisto() {
    const l = this.envios();
    if (l) try { localStorage.setItem(this.claveVisto(), firmaResultados(l)); } catch { /* modo privado */ }
  }

  protected readonly cobertura = signal<CoberturaProveedor | null>(null);
  private coberturaPedida = false;

  protected cargarCobertura() {
    const c = this.cred(), id = this.id();
    if (!c || !id || this.coberturaPedida) return;
    this.coberturaPedida = true;
    this.api.cobertura(c, id).subscribe({ next: r => this.cobertura.set(r), error: e => { this.coberturaPedida = false; this.fallo(e); } });
  }

  protected guardarCobertura(zonas: ZonaCobertura[]) {
    const c = this.cred(), id = this.id();
    if (!c || !id) return;
    this.cargando.set(true);
    this.error.set('');
    this.exito.set('');
    this.api.guardarCobertura(c, id, zonas).subscribe({
      next: r => { this.cobertura.set(r); this.cargando.set(false); this.exito.set('Tu cobertura quedó actualizada para todos tus clientes.'); },
      error: e => this.fallo(e),
    });
  }

  protected cargarHistorial() {
    const c = this.cred(), id = this.id();
    if (!c || !id || this.historialPedido) return;
    this.historialPedido = true;
    this.api.historial(c, id).subscribe({ next: l => this.historial.set(l), error: e => { this.historialPedido = false; this.historial.set([]); this.fallo(e); } });
  }

  protected tipos = (d: DocumentoProveedor) => d.tiposPermitidos.join(', ');
  protected accept = (d: DocumentoProveedor) => d.tiposPermitidos.map(t => `.${t}`).join(',');
  protected maxMb = (d: DocumentoProveedor) => Math.min(MAX_MB, Number(d.pesoMaximo) > 0 ? Number(d.pesoMaximo) : MAX_MB);
  protected fecha = (v: string) => (/^\d{4}-\d{2}-\d{2}/.test(v) ? fechaCorta(v) : v);
  protected readonly arrastrando = signal<string | null>(null);
  /** El archivo se sube al elegirlo; la fecha de vencimiento se lee del campo de ese documento. */
  protected fechaDe = (d: DocumentoProveedor) =>
    (this.host.nativeElement.shadowRoot?.getElementById('v-' + d.documentoSolicitaId) as HTMLInputElement | null)?.value || undefined;

  private enviar(exito: string, obs: Observable<FichaProveedor>, despues?: () => void) {
    this.cargando.set(true);
    this.error.set('');
    this.exito.set('');
    obs.subscribe({
      next: f => {
        despues?.();
        this.ficha.set(f);
        this.cargando.set(false);
        this.exito.set(exito);
        this.enviosPedidos = false;
        this.historialPedido = false;
        if (this.envios()) this.cargarEnvios();
        if (this.historial()) this.cargarHistorial();
      },
      error: e => this.fallo(e),
    });
  }

  protected guardar(s: SeccionProveedor) {
    const c = this.cred(), id = this.id(), datos = this.cambiosDe(s);
    if (!c || !id || !datos.length) return;
    this.enviar('Guardado en el borrador. Envía la sección cuando termines.', this.api.cargarDatos(c, id, s.seccionId, datos), () => this.limpiar(s));
  }

  /** Lo escrito y sin guardar se guarda antes de enviar; si algo no es válido no se envía nada. */
  protected enviarSeccion(s: SeccionProveedor) {
    const c = this.cred(), id = this.id(), datos = this.cambiosDe(s);
    if (!c || !id) return;
    const envio = this.api.enviarSeccion(c, id, s.seccionId);
    this.enviar(s.requiereAprobacion ? 'Sección enviada. El cliente la revisará y te avisaremos por correo.' : 'Listo, tu ficha quedó actualizada.',
      datos.length ? this.api.cargarDatos(c, id, s.seccionId, datos).pipe(switchMap(() => envio)) : envio, () => this.limpiar(s));
  }

  /** Mismo flujo para el selector y para arrastrar y soltar; la API vuelve a validar tipo, contenido y peso. */
  protected subirArchivo(doc: DocumentoProveedor, archivo: File | undefined) {
    const c = this.cred(), id = this.id(), vencimiento = doc.solicitaVencimiento ? this.fechaDe(doc) : undefined;
    if (!c || !id || this.cargando()) return;
    if (!archivo) return this.error.set('Selecciona un archivo.');
    if (!formatoPermitido(archivo.name, doc.tiposPermitidos)) return this.error.set(`Formato no permitido. Usa: ${doc.tiposPermitidos.join(', ').toUpperCase()}.`);
    if (archivo.size > this.maxMb(doc) * 1024 * 1024) return this.error.set(`El archivo supera los ${this.maxMb(doc)} MB.`);
    if (doc.solicitaVencimiento && !vencimiento) return this.error.set('Indica la fecha de vencimiento antes de subir el archivo.');
    this.enviar('Archivo guardado en el borrador. Envía la sección cuando termines.', this.api.cargarArchivo(c, id, doc.documentoSolicitaId, archivo, vencimiento));
  }
}
